import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { Row, Col, Form, Button, Spinner, Table, Alert, Modal } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import {
    FaSearch, FaArrowUp, FaArrowDown, FaChartLine,
    FaFilter, FaTimes, FaCalendarAlt, FaWallet,
    FaMoneyBillWave, FaShoppingCart, FaClipboardList,
    FaTruck, FaLightbulb, FaUserTie, FaEyeSlash,
    FaList, FaDollarSign, FaFileExport, FaBox
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const TYPE_CONFIG = {
    sale: { label: "Sale", icon: FaShoppingCart, badgeClass: "bg-success" },
    appointment: { label: "Appointment", icon: FaClipboardList, badgeClass: "bg-info" },
    vendor: { label: "Vendor", icon: FaTruck, badgeClass: "bg-warning" },
    purchase: { label: "Purchase", icon: FaTruck, badgeClass: "bg-warning" },
    daily_expense: { label: "Daily Expense", icon: FaLightbulb, badgeClass: "bg-danger" },
    utility_expense: { label: "Utility Expense", icon: FaLightbulb, badgeClass: "bg-danger" },
    salary_expense: { label: "Salary Expense", icon: FaUserTie, badgeClass: "bg-danger" },
    expense: { label: "Expense", icon: FaMoneyBillWave, badgeClass: "bg-danger" }
};

const formatDateForAPI = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '0.00';
    return amount.toFixed(2);
};

const formatDisplayDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

export default function FinanceReport() {
    const { t } = useContext(TranslatorContext);

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [summary, setSummary] = useState({ total_in: 0, total_out: 0, balance: 0 });
    const [selectedMonthDate, setSelectedMonthDate] = useState(new Date());
    const [reportPeriod, setReportPeriod] = useState({ start_date: '', end_date: '' });
    const [showFilters, setShowFilters] = useState(true);
    const today = new Date();

    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState([]);
    const [modalTotal, setModalTotal] = useState(0);
    const [modalTitle, setModalTitle] = useState('');

    const [selectedMonth, setSelectedMonth] = useState(
        `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`
    );

    const [accessCode, setAccessCode] = useState("");
    const [showAlert, setShowAlert] = useState(false);

    useEffect(() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const monthString = `${year}-${month}`;
        setSelectedMonth(monthString);
        handleFetchForMonth(monthString);
    }, []);

    useEffect(() => {
        if (accessCode === "Pet1122") {
            setShowAlert(true);
            const timer = setTimeout(() => setShowAlert(false), 3000);
            return () => clearTimeout(timer);
        } else {
            setShowAlert(false);
        }
    }, [accessCode]);

    const handleFetchForMonth = (targetMonthDate) => {
        if (!targetMonthDate) return;

        let year, month;

        if (typeof targetMonthDate === 'string') {
            const [yearStr, monthStr] = targetMonthDate.split('-');
            year = parseInt(yearStr);
            month = parseInt(monthStr) - 1;
        } else if (targetMonthDate instanceof Date) {
            year = targetMonthDate.getFullYear();
            month = targetMonthDate.getMonth();
        } else {
            const now = new Date();
            year = now.getFullYear();
            month = now.getMonth();
        }

        const firstDay = new Date(year, month, 1);
        firstDay.setHours(0, 0, 0, 0);

        const lastDay = new Date(year, month + 1, 0);
        lastDay.setHours(23, 59, 59, 999);

        const startDateStr = formatDateForAPI(firstDay);
        const endDateStr = formatDateForAPI(lastDay);

        setSelectedMonthDate(targetMonthDate);
        fetchReport(firstDay, lastDay);
    };

    const fetchReport = async (start, end) => {
        setLoading(true);
        try {
            const startDate = new Date(start);
            const endDate = new Date(end);

            const startDateStr = formatDateForAPI(startDate);
            const endDateStr = formatDateForAPI(endDate);

            const params = {
                start_date: startDateStr,
                end_date: endDateStr
            };

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/finance-report`,
                {
                    params,
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data?.status) {
                setReportData(response.data.data || []);
                setSummary(response.data.summary || { total_in: 0, total_out: 0, balance: 0 });
                setReportPeriod({
                    start_date: startDateStr,
                    end_date: endDateStr
                });
            }
        } catch (error) {
            console.error("Error fetching report:", error);
            alert(t('failed_to_load_report'));
        } finally {
            setLoading(false);
        }
    };

    const handleHideData = () => {
        setAccessCode("");
    };

    // Show all expenses (including purchases) in one modal
    const handleShowAllExpenses = () => {
        // Get all debit transactions (all expenses including purchases)
        const allExpenses = reportData.filter(item => 
            item.transaction_type === 'debit'
        );
        
        const total = allExpenses.reduce((sum, item) => sum + (item.amount || 0), 0);
        
        setModalTitle('All Expenses (Including Purchases)');
        setModalData(allExpenses);
        setModalTotal(total);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setModalData([]);
        setModalTotal(0);
    };

    // Export to CSV
    const handleExportCSV = () => {
        if (modalData.length === 0) return;
        
        const headers = ['Date', 'Type', 'Vendor/Name', 'Product Name', 'Quantity', 'Payment Method', 'Amount'];
        const csvRows = [
            headers.join(','),
            ...modalData.map(item => [
                formatDisplayDate(item.date),
                item.type,
                item.name || '',
                item.product_name || '',
                item.qty || '',
                item.payment_method || '-',
                item.amount
            ].join(','))
        ];
        
        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `expenses_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const totalIncomeFiltered = reportData
        .filter(t => t.transaction_type === 'credit')
        .reduce((sum, i) => sum + (i.amount || 0), 0);
    
    const totalExpenseFiltered = reportData
        .filter(t => t.transaction_type === 'debit')
        .reduce((sum, e) => sum + (e.amount || 0), 0);
    
    const totalPurchases = reportData
        .filter(t => t.type === 'purchase')
        .reduce((sum, p) => sum + (p.amount || 0), 0);
    
    const totalOtherExpenses = totalExpenseFiltered - totalPurchases;
    const netProfit = summary.balance;

    const StatCard = ({ title, value, type, icon: Icon, onClick, subtitle }) => {
        const isPositive = type === 'income' || (type === 'profit' && value >= 0);
        const colorClass = type === 'income' ? 'success' : type === 'expense' ? 'danger' : isPositive ? 'success' : 'danger';
        const isClickable = type === 'expense';

        return (
            <Col xl={4} md={6}>
                <div 
                    className={`card h-100 border-0 shadow-sm border-start border-${colorClass} border-4 ${isClickable ? 'cursor-pointer hover-shadow' : ''}`}
                    onClick={onClick}
                    style={isClickable ? { 
                        cursor: 'pointer', 
                        transition: 'all 0.2s ease',
                        transform: 'scale(1)'
                    } : {}}
                    onMouseEnter={(e) => {
                        if (isClickable) {
                            e.currentTarget.style.transform = 'scale(1.02)';
                            e.currentTarget.style.boxShadow = '0 0.5rem 1rem rgba(0,0,0,0.15)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (isClickable) {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                        }
                    }}
                >
                    <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="text-muted mb-2 small text-uppercase fw-bold tracking-wider">
                                    {title}
                                </h6>
                                <h2 className={`mb-1 text-${colorClass} fw-extrabold`}>
                                    {type === 'profit' && value >= 0 ? '+' : type === 'profit' && value < 0 ? '-' : ''}
                                    {formatCurrency(Math.abs(value))}
                                </h2>
                                <small className="text-muted d-block mt-1">
                                    {subtitle || (
                                        type === 'expense' ? 'Click to view all expenses' :
                                        type === 'income' ? 'All revenue sources' : 'After all expenses'
                                    )}
                                </small>
                            </div>
                            <div className={`bg-${colorClass} bg-opacity-10 p-3 rounded-3`}>
                                <Icon className={`text-${colorClass}`} size={24} />
                            </div>
                        </div>
                    </div>
                </div>
            </Col>
        );
    };

    // Transaction Table Component
    const TransactionTable = () => (
        <div className="card">
            <div className="card-header bg-transparent d-flex justify-content-between align-items-center border-bottom pb-3">
                <div>
                    <h5 className="mb-0 fw-semibold">
                        <FaChartLine className="me-2 text-primary" />
                        {t('All Transactions')}
                    </h5>
                    <small className="text-muted">{t('Income and expense transactions')}</small>
                </div>
            </div>
            <div className="table-responsive">
                <Table responsive hover className="mb-0">
                    <thead>
                        <tr>
                            <th className="ps-3">#</th>
                            <th>{t('Type')}</th>
                            <th>{t('Transaction Name / Details')}</th>
                            <th>{t('Date')}</th>
                            <th>{t('Payment Method')}</th>
                            <th className="text-end pe-3">{t('Amount')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="text-center py-5 text-muted">
                                    <FaWallet size={40} className="mb-2 opacity-25" />
                                    <p className="mb-0">{t('No transactions found')}</p>
                                </td>
                            </tr>
                        ) : (
                            reportData.map((item, idx) => {
                                const typeConfig = TYPE_CONFIG[item.type] || TYPE_CONFIG.expense;
                                const Icon = typeConfig.icon;
                                const isPurchase = item.type === 'purchase';

                                return (
                                    <tr key={idx}>
                                        <td className="ps-3 fw-medium">{idx + 1}</td>
                                        <td>
                                            <span className={`badge ${typeConfig.badgeClass} d-inline-flex align-items-center gap-1 px-2 py-1`}>
                                                <Icon size={14} />
                                                <span>{isPurchase ? 'Purchase' : t(typeConfig.label)}</span>
                                            </span>
                                        </td>
                                        <td>
                                            {isPurchase ? (
                                                <div>
                                                    <div className="fw-medium">{item.name}</div>
                                                    {item.product_name && (
                                                        <small className="text-muted">
                                                            <FaBox className="me-1" size={10} />
                                                            {item.product_name} {item.qty && `(x${item.qty})`}
                                                        </small>
                                                    )}
                                                </div>
                                            ) : (
                                                <div>
                                                    <div className="fw-medium">{item.name}</div>
                                                    <small className="text-muted text-capitalize">
                                                        {item.type?.replace(/_/g, ' ')}
                                                    </small>
                                                </div>
                                            )}
                                        </td>
                                        <td>{formatDisplayDate(item.date)}</td>
                                        <td>
                                            <span className="badge bg-light text-dark border">
                                                {isPurchase ? '-' : item.payment_method || 'Cash'}
                                            </span>
                                        </td>
                                        <td className="text-end pe-3">
                                            <span className={`fw-bold ${item.transaction_type === 'credit' ? 'text-success' : 'text-danger'}`}>
                                                {item.transaction_type === 'credit' ? '+' : '-'} {formatCurrency(item.amount)}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="border-top">
                            <td colSpan="5" className="text-end fw-bold pe-3">{t('Total Balance')}:</td>
                            <td className={`text-end pe-3 fw-bold ${netProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                                {netProfit >= 0 ? '+' : '-'}{formatCurrency(Math.abs(netProfit))}
                            </td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );

    // Single Expense Modal - Shows ALL expenses including purchases with proper details
    const ExpenseModal = () => (
        <Modal show={showModal} onHide={handleCloseModal} size="xl">
            <Modal.Header closeButton className="border-bottom p-2">
                <Modal.Title className="d-flex align-items-center">
                    <FaList className="me-2 text-danger" size={20} />
                    {modalTitle}
                    <span className="ms-3 badge bg-secondary">
                        Total: {formatCurrency(modalTotal)}
                    </span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-3">
                {modalData.length === 0 ? (
                    <div className="text-center py-5">
                        <FaWallet size={50} className="mb-3 text-muted opacity-25" />
                        <p className="text-muted">No expenses found for this period</p>
                    </div>
                ) : (
                    <>
                        <div className="table-responsive">
                            <Table responsive hover className="mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Vendor / Name</th>
                                        <th>Product Details</th>
                                        <th>Qty</th>
                                        <th>Payment Method</th>
                                        <th className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modalData.map((item, idx) => {
                                        const typeConfig = TYPE_CONFIG[item.type] || TYPE_CONFIG.expense;
                                        const Icon = typeConfig.icon;
                                        const isPurchase = item.type === 'purchase';

                                        return (
                                            <tr key={idx}>
                                                <td>{idx + 1}</td>
                                                <td>{formatDisplayDate(item.date)}</td>
                                                <td>
                                                    <span className={`badge ${typeConfig.badgeClass} d-inline-flex align-items-center gap-1 px-2 py-1`}>
                                                        <Icon size={12} />
                                                        <span>{isPurchase ? 'Purchase' : item.type?.replace(/_/g, ' ')}</span>
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="fw-medium">{item.name || 'N/A'}</div>
                                                </td>
                                                <td>
                                                    {isPurchase ? (
                                                        <div>
                                                            {item.product_name ? (
                                                                <span className="fw-medium">{item.product_name}</span>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {isPurchase && item.qty ? (
                                                        <span className="badge bg-secondary">{item.qty}</span>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className="badge bg-light text-dark border">
                                                        {isPurchase ? '-' : item.payment_method || 'Cash'}
                                                    </span>
                                                </td>
                                                <td className="text-end fw-bold text-danger">
                                                    {formatCurrency(item.amount)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot className="bg-light">
                                    <tr>
                                        <td colSpan="7" className="text-end fw-bold pe-3">
                                            Total Expenses (Including Purchases):
                                        </td>
                                        <td className="text-end fw-bold text-danger">
                                            {formatCurrency(modalTotal)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan="7" className="text-end fw-bold pe-3 text-warning">
                                            Purchases Total:
                                        </td>
                                        <td className="text-end fw-bold text-warning">
                                            {formatCurrency(modalData.filter(item => item.type === 'purchase').reduce((sum, item) => sum + (item.amount || 0), 0))}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan="7" className="text-end fw-bold pe-3 text-danger">
                                            Other Expenses Total:
                                        </td>
                                        <td className="text-end fw-bold text-danger">
                                            {formatCurrency(modalData.filter(item => item.type !== 'purchase').reduce((sum, item) => sum + (item.amount || 0), 0))}
                                        </td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="border-top">
                <Button variant="secondary" onClick={handleCloseModal}>
                    Close
                </Button>
                {modalData.length > 0 && (
                    <Button variant="primary" onClick={handleExportCSV} className="d-flex align-items-center gap-2">
                        <FaFileExport /> Export to CSV
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );

    return (
        <PageLayout>
            <div className="container-fluid py-3">
                {/* Floating Success Alert Toast */}
                {showAlert && (
                    <Alert variant="success" className="position-fixed top-0 end-0 m-3 shadow" style={{ zIndex: 1050 }} onClose={() => setShowAlert(false)} dismissible>
                        {t('Access Granted! Financial summary unlocked.')}
                    </Alert>
                )}

                <div className="mb-4">
                    <h2 className="mb-1 fw-bold">
                        <FaChartLine className="me-2 text-primary" />
                        {t('Finance Report')}
                    </h2>
                    <small className="text-muted">
                        {reportPeriod.start_date && reportPeriod.end_date && (
                            <>Period: {formatDisplayDate(reportPeriod.start_date)} - {formatDisplayDate(reportPeriod.end_date)}</>
                        )}
                    </small>
                </div>

                <div className="card mb-4">
                    <div className="card-body">
                        {showFilters && (
                            <div className="row g-3 align-items-end">
                                <div className="col-md-4">
                                    <Form.Label className="fw-semibold small text-muted mb-1 d-block">
                                        <FaCalendarAlt className="me-1" size={12} />
                                        {t("MONTH")}
                                    </Form.Label>
                                    <input
                                        type="month"
                                        className="form-control"
                                        value={selectedMonth}
                                        onChange={(e) => {
                                            const monthValue = e.target.value;
                                            setSelectedMonth(monthValue);
                                            handleFetchForMonth(monthValue);
                                        }}
                                    />
                                </div>
                                <div className="col-md-5">
                                    <Form.Label className="fw-semibold small text-muted mb-1 d-block">
                                        {t("ACCESS CODE")}
                                    </Form.Label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        placeholder={t("Enter access code")}
                                        value={accessCode}
                                        onChange={(e) => setAccessCode(e.target.value)}
                                    />
                                </div>
                                {accessCode === "Pet1122" && (
                                    <div className="col-md-3">
                                        <Button
                                            variant="secondary"
                                            className="w-100 d-flex align-items-center justify-content-center gap-2"
                                            onClick={handleHideData}
                                        >
                                            <FaEyeSlash /> {t("Hide Data")}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {accessCode === "Pet1122" ? (
                    <>
                        <Row className="g-3 mb-4">
                            <StatCard
                                title={t('Total Income')}
                                value={summary.total_in}
                                type="income"
                                icon={FaArrowDown}
                            />
                            <StatCard
                                title={t('Total Expenses')}
                                value={summary.total_out}
                                type="expense"
                                icon={FaArrowUp}
                                onClick={handleShowAllExpenses}
                            />
                            <StatCard
                                title={t('Net Profit / Loss')}
                                value={summary.balance}
                                type="profit"
                                icon={summary.balance >= 0 ? FaArrowDown : FaArrowUp}
                            />
                        </Row>

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">{t('Loading...')}</p>
                            </div>
                        ) : (
                            <TransactionTable />
                        )}
                    </>
                ) : (
                    <div className="text-center py-5 text-muted border rounded bg-light shadow-sm">
                        <FaEyeSlash size={40} className="mb-3 opacity-25" />
                        <p className="mb-0 fw-medium">
                            {t('Please enter a valid access code to unlock sensitive financial matrices.')}
                        </p>
                    </div>
                )}

                {/* Single Expense Modal - Shows ALL expenses including purchases */}
                <ExpenseModal />
            </div>

            <style jsx>{`
                .hover-shadow {
                    transition: all 0.3s ease;
                }
                .hover-shadow:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
                }
                .cursor-pointer {
                    cursor: pointer;
                }
                .tracking-wider {
                    letter-spacing: 0.05em;
                }
                .bg-opacity-10 {
                    --bs-bg-opacity: 0.1;
                }
            `}</style>
        </PageLayout>
    );
}